/* The HTTP client.
 *
 * One concern dominates this file: REFRESH TOKENS ARE SINGLE USE AND ROTATED,
 * and the server treats replay of a spent one as theft — it burns the whole
 * token family and signs the person out everywhere.
 *
 * A feed page fires several requests at once. If the access token expires
 * mid-flight, every one of them gets a 401 at the same moment. The naive
 * client then sends N refresh requests with the SAME refresh token: the first
 * rotates it, the rest are replays, and the reuse detector logs the user out.
 *
 * The fix is the single-flight promise below — the first 401 starts a refresh,
 * every other waits on that same promise, and all of them retry with the new
 * token. It is the difference between "session lasts a week" and "session dies
 * randomly", and it cannot be tested by clicking around.
 */
import type { Session } from '@/types/api'

/** Which API this build talks to.
 *
 * Each web host has a matching API host — app-trueway → api-trueway,
 * dev-app-trueway → dev-api-trueway — so the origin is derived from where the
 * page was served rather than hard-coded. That way one bundle works in both
 * environments and cannot be deployed pointing at the wrong database.
 *
 * VITE_API_BASE overrides it, for local work against a chosen backend.
 */
function resolveApiBase(): string {
  const override = import.meta.env.VITE_API_BASE
  if (override) return override

  const host = window.location.hostname

  // app-trueway.cleverstack.in    → api-trueway.cleverstack.in
  // dev-app-trueway.cleverstack.in → dev-api-trueway.cleverstack.in
  const matched = host.match(/^(dev-)?(app|admin)-trueway\.(.+)$/)
  if (matched) {
    const [, devPrefix = '', , domain] = matched
    return `https://${devPrefix}api-trueway.${domain}/api/v1`
  }

  // The legacy single host, and the local dev server (which proxies /api).
  return '/api/v1'
}

const BASE = resolveApiBase()

/** A path the API returned (such as a live stream's) as a full address.
 *  The API answers with paths under /api/v1; on a host where the API lives
 *  elsewhere, the player needs that host in front of them. */
export function apiUrl(path: string): string {
  return BASE.replace(/\/api\/v1$/, '') + path
}

/** The same, for a WebSocket: wss:// on https, ws:// locally. */
export function apiSocketUrl(path: string): string {
  const base = BASE.startsWith('http') ? BASE : `${window.location.origin}${BASE}`
  return base.replace(/^http/, 'ws') + path
}

const ACCESS_KEY = 'cig.access'
const REFRESH_KEY = 'cig.refresh'

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY)
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY)
  },
  save(session: Session) {
    localStorage.setItem(ACCESS_KEY, session.access_token)
    localStorage.setItem(REFRESH_KEY, session.refresh_token)
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

/** A failed request, carrying the server's problem+json detail. */
export class ApiError extends Error {
  status: number
  code?: string
  fieldErrors: Record<string, string>

  constructor(
    message: string,
    status: number,
    code?: string,
    fieldErrors: Record<string, string> = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.fieldErrors = fieldErrors
  }

  /** True when retrying might work — the caller decides whether to offer it. */
  get isTransient() {
    return this.status === 0 || this.status >= 500
  }
}

/** Fired when refresh fails and the session is genuinely over. The app shell
 *  listens and routes to sign-in; the client itself does not know about routing. */
export const SESSION_EXPIRED = 'cig:session-expired'

function signOut() {
  tokens.clear()
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED))
}

/* --------------------------------------------------------- single-flight */

/** The in-progress refresh, if any. Every concurrent 401 awaits this one
 *  promise rather than starting its own. Null when no refresh is running. */
let refreshing: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  // Already running: join it. This is the whole point of the file.
  if (refreshing) return refreshing

  refreshing = (async () => {
    const refresh = tokens.refresh
    if (!refresh) {
      signOut()
      return null
    }

    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      })

      if (!res.ok) {
        // 401 here means the refresh token is spent, revoked or reused. There
        // is no recovering from it in the client — the session is over.
        signOut()
        return null
      }

      const session = (await res.json()) as Session
      tokens.save(session)
      return session.access_token
    } catch {
      // Network failure, not an auth failure. Keep the tokens: the user is
      // probably offline and their session is still valid.
      return null
    } finally {
      // Cleared in `finally` so a failed refresh does not wedge every later
      // request on a permanently rejected promise.
      refreshing = null
    }
  })()

  return refreshing
}

/* ------------------------------------------------------------- requests */

interface RequestOptions {
  method?: string
  body?: unknown
  auth?: boolean
  signal?: AbortSignal
}

async function parseError(res: Response): Promise<ApiError> {
  let detail = 'Something went wrong.'
  let code: string | undefined
  const fieldErrors: Record<string, string> = {}

  try {
    const data = await res.json()
    detail = data?.detail || data?.title || detail
    code = data?.code
    if (Array.isArray(data?.errors)) {
      for (const e of data.errors) {
        if (e?.field) fieldErrors[e.field] = e.message
      }
    }
  } catch {
    // A non-JSON error body (a proxy 502 page, say). The status is all we have.
    detail = res.statusText || detail
  }

  return new ApiError(detail, res.status, code, fieldErrors)
}

export async function request<T>(
  path: string,
  { method = 'GET', body, auth = true, signal }: RequestOptions = {},
): Promise<T> {
  async function send(token: string | null): Promise<Response> {
    return fetch(`${BASE}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  }

  let res: Response
  try {
    res = await send(auth ? tokens.access : null)
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw err
    throw new ApiError('You appear to be offline.', 0, 'network')
  }

  // One retry, and only for an expired access token. Retrying anything else
  // risks repeating a mutation the server already applied.
  if (res.status === 401 && auth && tokens.refresh) {
    const fresh = await refreshAccessToken()
    if (!fresh) throw await parseError(res)
    try {
      res = await send(fresh)
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') throw err
      throw new ApiError('You appear to be offline.', 0, 'network')
    }
  }

  if (!res.ok) throw await parseError(res)

  // 204 and other empty bodies.
  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** Upload one file.
 *
 *  Separate from `request` because a multipart body must NOT carry a
 *  Content-Type header we set ourselves — the browser has to add it along with
 *  the multipart boundary, and setting it by hand produces a request the server
 *  cannot parse.
 *
 *  Shares the same 401-and-retry path, so an expired token during a slow upload
 *  refreshes rather than failing.
 */
export async function uploadFile<T>(
  path: string,
  file: File,
  /** Extra form fields sent alongside it — `kind` for an upload that is a
   *  document rather than an image. */
  fields?: Record<string, string>,
): Promise<T> {
  const form = new FormData()
  form.append('file', file)
  for (const [key, value] of Object.entries(fields ?? {})) {
    form.append(key, value)
  }

  async function send(token: string | null): Promise<Response> {
    return fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    })
  }

  let res: Response
  try {
    res = await send(tokens.access)
  } catch {
    throw new ApiError('You appear to be offline.', 0, 'network')
  }

  if (res.status === 401 && tokens.refresh) {
    const fresh = await refreshAccessToken()
    if (!fresh) throw await parseError(res)
    res = await send(fresh)
  }

  if (!res.ok) throw await parseError(res)
  return (await res.json()) as T
}

/** Send a file straight to storage, reporting progress.
 *
 *  For videos, which never pass through the API: the server hands out a
 *  one-time upload link for exactly this file, and the bytes go there. No
 *  Authorization header — the link is its own permission, and sending our
 *  token to storage would hand it to a host that has no use for it.
 *
 *  XMLHttpRequest rather than fetch, because fetch still cannot report
 *  upload progress, and a bar that sits at nothing for two minutes while a
 *  300 MB video goes up reads as broken.
 */
export function putFile(
  url: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total)
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new ApiError('The upload did not go through.', xhr.status))
    xhr.onerror = () =>
      reject(new ApiError('The upload stopped. Check the connection and try again.', 0, 'network'))
    xhr.send(file)
  })
}

/** A last word from a page that is going away.
 *
 *  An ordinary request made while a tab closes is cancelled with the page.
 *  `keepalive` lets the browser finish it afterwards — and unlike
 *  sendBeacon, it can carry our Authorization header. Nothing waits for
 *  the answer and nothing can: there is no page left to tell. No retry
 *  on 401 either, for the same reason.
 */
export function farewell(path: string): void {
  try {
    void fetch(`${BASE}${path}`, {
      method: 'POST',
      keepalive: true,
      headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
    }).catch(() => undefined)
  } catch {
    // A browser that refuses keepalive loses the courtesy; the server
    // notices the silence on its own.
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
}
